import { PROJECT_MODEL_LIMIT } from 'src/constants/limits';
import {
  OWL_DATATYPE_PROPERTY_IRI,
  OWL_OBJECT_PROPERTY_IRI,
  RDFS_DOMAIN_IRI,
  RDFS_RANGE_IRI,
  RDFS_SUB_PROPERTY_OF_IRI,
  SCHEMA_ORG_DOMAIN_INCLUDES_IRI,
  SCHEMA_ORG_HTTP_DOMAIN_INCLUDES_IRI,
  SCHEMA_ORG_HTTP_RANGE_INCLUDES_IRI,
  SCHEMA_ORG_HTTP_SUPERSEDED_BY_IRI,
  SCHEMA_ORG_RANGE_INCLUDES_IRI,
  SCHEMA_ORG_SUPERSEDED_BY_IRI,
} from 'src/templates/queries/iris';
import { BindingResult } from 'src/templates/queries/types';

export type ModelPropertyBindingResult = {
  domain?: BindingResult;
  domainIncludes?: BindingResult;
  property?: BindingResult;
  range?: BindingResult;
  rangeIncludes?: BindingResult;
  parent?: BindingResult;
  isDatatype?: BindingResult;
};

/**
 * Determines the properties with hierarchies for a given model.
 *
 * Used to snapshot the model information of a given reasoning schema.
 *
 * **Call Frequency**
 *
 * Called once when a model is added to a project.
 *
 * **Context**
 * - Named graphs: set by the user (default is `reasoning.schema.graphs` value,
 *   which by default is `tag:stardog:api:context:local`)
 * - Reasoning: always off
 *
 * **Example**
 * ```sparql
# Model Properties
SELECT DISTINCT ?domain ?domainIncludes ?property ?range ?rangeIncludes ?parent ?isDatatype
WHERE {
  {
    ?property <http://www.w3.org/2000/01/rdf-schema#domain> ?domain
  } UNION {
    ?property <http://www.w3.org/2000/01/rdf-schema#range> ?range
  } UNION {
    ?property <https://schema.org/domainIncludes>
            | <http://schema.org/domainIncludes>
            ?domainIncludes
  } UNION {
    ?property <https://schema.org/rangeIncludes>
            | <http://schema.org/rangeIncludes>
            ?rangeIncludes
  } OPTIONAL {
    ?property <http://www.w3.org/2000/01/rdf-schema#subPropertyOf>
            | <https://schema.org/supersededBy>
            | <http://schema.org/supersededBy>
            ?parent
    FILTER (?subject != ?parent && isIRI(?parent))
  } OPTIONAL {
    ?property a <http://www.w3.org/2002/07/owl#DatatypeProperty>
    BIND(true as ?isDatatype)
  } OPTIONAL {
    ?property a <http://www.w3.org/2002/07/owl#ObjectProperty>
    BIND(false as ?isDatatype)
  }
}
LIMIT 100000
 * ```
 */
export const modelPropertiesQuery = `# Model Properties
SELECT DISTINCT ?domain ?domainIncludes ?property ?range ?rangeIncludes ?parent ?isDatatype
WHERE {
  {
    ?property <${RDFS_DOMAIN_IRI}> ?domain
  } UNION {
    ?property <${RDFS_RANGE_IRI}> ?range
  } UNION {
    ?property <${SCHEMA_ORG_DOMAIN_INCLUDES_IRI}>
            | <${SCHEMA_ORG_HTTP_DOMAIN_INCLUDES_IRI}>
            ?domainIncludes
  } UNION {
    ?property <${SCHEMA_ORG_RANGE_INCLUDES_IRI}>
            | <${SCHEMA_ORG_HTTP_RANGE_INCLUDES_IRI}>
            ?rangeIncludes
  } OPTIONAL {
    ?property <${RDFS_SUB_PROPERTY_OF_IRI}>
            | <${SCHEMA_ORG_SUPERSEDED_BY_IRI}>
            | <${SCHEMA_ORG_HTTP_SUPERSEDED_BY_IRI}>
            ?parent
    FILTER (?subject != ?parent && isIRI(?parent))
  } OPTIONAL {
    ?property a <${OWL_DATATYPE_PROPERTY_IRI}>
    BIND(true as ?isDatatype)
  } OPTIONAL {
    ?property a <${OWL_OBJECT_PROPERTY_IRI}>
    BIND(false as ?isDatatype)
  }
}
LIMIT ${PROJECT_MODEL_LIMIT}`;
